import { Routes, Route, Navigate, Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { fetchMe, refreshSession, logout, type SessionUser } from './auth';
import { ToastProvider } from './lib/toast';
import { ErrorBoundary } from './lib/ErrorBoundary';

import LoginPage from './pages/Login';
import MagicVerifyPage from './pages/MagicVerify';
import DashboardPage from './pages/Dashboard';
import JobsPage from './pages/Jobs';
import CustomersPage from './pages/Customers';
import QuotesPage from './pages/Quotes';
import ProductsPage from './pages/Products';
import SubscriptionsPage from './pages/Subscriptions';
import UsersPage from './pages/Users';
import BoilerManualsPage from './pages/BoilerManuals';
import CertsPage from './pages/Certs';
import CertGroupsPage from './pages/CertGroups';
import CertSchemasListPage, { CertSchemaEditorPage } from './pages/CertSchemas';
import CalculatorsPage from './pages/Calculators';
import ServiceControlsPage from './pages/ServiceControls';
import { VoiceDialogListPage, VoiceDialogEditorPage, LookupViewerPage, VoiceDialogFlowPage } from './pages/VoiceDialog';
import { VoiceSessionsListPage, VoiceSessionDetailPage } from './pages/VoiceSessions';
import Placeholder from './pages/Placeholder';

type NavItem = { to: string; label: string; icon: string };
type NavGroup = { title: string; items: NavItem[] };

const navGroups: NavGroup[] = [
  { title: 'Overview',   items: [{ to: '/dashboard', label: 'Dashboard', icon: '📊' }] },
  { title: 'Management', items: [
    { to: '/customers',     label: 'Customers',     icon: '👥' },
    { to: '/products',      label: 'Products',      icon: '📦' },
    { to: '/subscriptions', label: 'Subscriptions', icon: '💳' },
    { to: '/payments',      label: 'Payments',      icon: '💷' },
  ] },
  { title: 'Controls', items: [
    { to: '/users',            label: 'Users & Access',   icon: '🔐' },
    { to: '/service-controls', label: 'Service Controls', icon: '⚙️' },
    { to: '/referrals',        label: 'Referrals',        icon: '🎁' },
    { to: '/audit-log',        label: 'Audit Log',        icon: '📜' },
  ] },
  { title: 'Finance',    items: [{ to: '/finance', label: 'Finance', icon: '🔥' }] },
  { title: 'Fulfilment', items: [
    { to: '/jobs',          label: 'Jobs',            icon: '🧰' },
    { to: '/quotes',        label: 'Quotes',          icon: '📋' },
    { to: '/calculators',   label: 'Calculators',     icon: '🔢' },
    { to: '/dispatch',      label: 'Dispatch',        icon: '🗺️' },
    { to: '/certificates',  label: 'Certificates',    icon: '📜' },
    { to: '/cert-groups',   label: 'Cert Groups',     icon: '📁' },
    { to: '/cert-schemas',  label: 'Cert Schemas',    icon: '🧬' },
    { to: '/voice-dialog',  label: 'Voice Dialog',    icon: '🎤' },
    { to: '/voice-sessions', label: 'Voice Sessions', icon: '🎬' },
    { to: '/materials',     label: 'Materials (DPS)', icon: '🔧' },
    { to: '/boiler-manuals', label: 'Boiler Manuals', icon: '🔥' },
    { to: '/diagnostics',   label: 'Diagnostics',     icon: '🔍' },
    { to: '/website',       label: 'Website Builder', icon: '🌐' },
  ] },
  { title: 'System', items: [{ to: '/settings', label: 'Settings', icon: '🛠️' }] },
];

function Shell({ user, onSignedOut }: { user: SessionUser; onSignedOut: () => void }) {
  const loc = useLocation();
  const nav = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close drawer when user navigates (mobile)
  useEffect(() => { setSidebarOpen(false); }, [loc.pathname]);

  async function onLogout() {
    await logout();
    onSignedOut();
    nav('/login');
  }

  const currentPage = [...navGroups.flatMap(g => g.items)].find(
    i => loc.pathname === i.to || loc.pathname.startsWith(i.to + '/')
  );

  return (
    <div className="min-h-screen flex bg-ink-bg text-ink-text">
      {/* Mobile top bar — hamburger + page title */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-ink-card border-b border-ink-border z-30 flex items-center px-4 gap-3">
        <button onClick={() => setSidebarOpen(true)}
                className="p-1.5 -ml-1.5 text-ink-text hover:bg-ink-hover rounded"
                aria-label="Open menu">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <line x1="3" y1="7" x2="21" y2="7"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="17" x2="21" y2="17"/>
          </svg>
        </button>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-brand-600 flex items-center justify-center text-white font-bold text-xs">O</div>
          <div className="text-sm font-bold">OnlyTrade</div>
          {currentPage && <span className="text-ink-muted text-sm">· {currentPage.label}</span>}
        </div>
      </div>

      {/* Sidebar backdrop on mobile */}
      {sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)}
             className="lg:hidden fixed inset-0 bg-black/60 z-40" />
      )}

      {/* Sidebar — drawer on mobile, fixed on desktop */}
      <aside className={`
        fixed lg:sticky top-0 left-0 h-screen w-64 bg-ink-card border-r border-ink-border
        flex flex-col z-50 transition-transform duration-200 ease-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="px-5 py-5 border-b border-ink-border flex items-center gap-2 shrink-0">
          <div className="w-8 h-8 rounded bg-brand-600 flex items-center justify-center text-white font-bold">O</div>
          <div className="flex-1">
            <div className="text-base font-bold leading-tight">OnlyTrade</div>
            <div className="text-[10px] text-ink-muted uppercase tracking-wider">Product Admin</div>
          </div>
          <button onClick={() => setSidebarOpen(false)} aria-label="Close menu"
                  className="lg:hidden p-1 text-ink-muted hover:text-ink-text">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="5" y1="5" x2="19" y2="19"/><line x1="19" y1="5" x2="5" y2="19"/>
            </svg>
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3">
          {navGroups.map(g => (
            <div key={g.title} className="mb-2">
              <div className="text-[10px] font-bold uppercase text-ink-muted px-5 pt-3 pb-1.5 tracking-wider">{g.title}</div>
              {g.items.map(it => {
                const active = loc.pathname === it.to || loc.pathname.startsWith(it.to + '/');
                return (
                  <Link key={it.to} to={it.to}
                    className={`flex items-center gap-3 px-5 py-2 text-sm transition-colors ${
                      active
                        ? 'bg-brand-600/15 text-brand-400 font-medium border-l-2 border-brand-600 pl-[18px]'
                        : 'text-ink-text/85 hover:bg-ink-hover border-l-2 border-transparent'
                    }`}>
                    <span className="w-4 text-center text-[13px]">{it.icon}</span>
                    <span>{it.label}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="px-5 py-3 border-t border-ink-border shrink-0">
          <div className="text-xs text-ink-text truncate" title={user.email}>{user.display_name || user.email}</div>
          <div className="text-[10px] text-ink-muted mb-2">{user.role}</div>
          <button onClick={onLogout} className="text-xs text-ink-muted hover:text-ink-text transition-colors">🚪 Logout</button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-auto pt-14 lg:pt-0">
        {/* Error boundary keyed on path — a render error on one page resets
            when the engineer navigates to another. Sidebar + topbar stay
            mounted outside the boundary so the shell never goes white. */}
        <ErrorBoundary key={loc.pathname} label={'Couldn’t render: ' + (currentPage?.label || loc.pathname)}>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  );
}

function RequireAuth({ user, setUser }: { user: SessionUser | null; setUser: (u: SessionUser | null) => void }) {
  if (!user) return <Navigate to="/login" replace />;
  return <Shell user={user} onSignedOut={() => setUser(null)} />;
}

export default function App() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let u = await fetchMe();
      if (!u) {
        const r = await refreshSession();
        u = r?.user || null;
      }
      if (!cancelled) { setUser(u); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-bg text-ink-muted text-sm">
        Loading…
      </div>
    );
  }

  return (
    <ToastProvider>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
        <Route path="/auth/verify" element={<MagicVerifyPage />} />
        <Route element={<RequireAuth user={user} setUser={setUser} />}>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard"         element={<DashboardPage />} />
          <Route path="/customers"         element={<CustomersPage />} />
          <Route path="/products"          element={<ProductsPage />} />
          <Route path="/subscriptions"     element={<SubscriptionsPage />} />
          <Route path="/payments"          element={<Placeholder title="Payments" hint="Stripe-backed payment history. Wired to /api/payments." />} />
          <Route path="/users"             element={<UsersPage />} />
          <Route path="/service-controls"  element={<ServiceControlsPage />} />
          <Route path="/referrals"         element={<Placeholder title="Referrals" hint="Referral program settings & ledger at /api/referrals." />} />
          <Route path="/audit-log"         element={<Placeholder title="Audit Log" hint="Every admin action at /api/audit-log." />} />
          <Route path="/finance"           element={<Placeholder title="Finance" hint="P&L, receivables, cost entries, CSV export." />} />
          <Route path="/jobs"              element={<JobsPage />} />
          <Route path="/quotes"            element={<QuotesPage />} />
          <Route path="/calculators"       element={<CalculatorsPage />} />
          <Route path="/dispatch"          element={<Placeholder title="Dispatch Board" hint="GPS tracking + Haversine routing." />} />
          <Route path="/certificates"      element={<CertsPage />} />
          <Route path="/cert-groups"       element={<CertGroupsPage />} />
          <Route path="/cert-schemas"      element={<CertSchemasListPage />} />
          <Route path="/cert-schemas/:id"  element={<CertSchemaEditorPage />} />
          <Route path="/voice-dialog"           element={<VoiceDialogListPage />} />
          <Route path="/voice-dialog/flow"      element={<VoiceDialogFlowPage />} />
          <Route path="/voice-dialog/:section"  element={<VoiceDialogEditorPage />} />
          <Route path="/voice-dialog/lookups/:name" element={<LookupViewerPage />} />
          <Route path="/voice-sessions"                 element={<VoiceSessionsListPage />} />
          <Route path="/voice-sessions/:conversationId" element={<VoiceSessionDetailPage />} />
          <Route path="/materials"         element={<Placeholder title="Materials (DPS)" hint="271 DPS parts mapped to 624 boilers." />} />
          <Route path="/boiler-manuals"    element={<BoilerManualsPage />} />
          <Route path="/diagnostics"       element={<Placeholder title="Diagnostics" hint="624 boiler models · 39 manufacturers." />} />
          <Route path="/website"           element={<Placeholder title="Website Builder" hint="Ported from existing OnlyTrade website builder." />} />
          <Route path="/settings"          element={<Placeholder title="Settings" hint="Business profile, team, subscription (read-only from Core)." />} />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </ToastProvider>
  );
}
